import { Component } from "../component"
import Swiper from "swiper"
import anime from "animejs"
import { scrollLoop } from "../helpers/scroll-loop"
import { iterable } from "../helpers/iterable"
import { debounce } from "../helpers/debounce"
import { $ } from "dom7"
import Vimeo from "@vimeo/player"

export class Product extends Component {
  constructor(props) {
    super(props)

    this.dynamicClasses = {
      stickyBarVisible: "-sticky-bar-visible",
      zoom: "-zoom",
      customCursorVisible: "-custom-cursor-visible",
      zoomImgLoaded: "-zoom-img-loaded",
      soldOut: "-sold-out",
      visible: "-visible",
      playing: "-playing",
      open: "-open",
      activated: "-activated",
      fixed: "-fixed",
      sticky: "-sticky",
    }
    this.videoObserverOptions = {
      threshold: 0.0001,
      rootMargin: "300px",
    }
  }

  ui() {
    return {
      swiperContainer: ".swiper",
      productColumns: ".product-columns",
      productImages: ".product-images-item",
      productVideos: ".product-thumbnail-video",
      productInfo: ".product-info",
      productFormInner: ".product-form-inner",
      productSKU: ".product-sku",
      soldOutIndicator: ".product-form-sold-out-indicator",
      variantRadioInputs: ".variant-radios-item-input",
      variantRadioInputsValues: ".variant-radios-value",
      variantRadiosNavButtons: ".variant-radios-nav-button",
      variantRadiosWrappers: ".variant-radios-items-wrapper",
      variantDropdownToggle: ".variant-dropdown-toggle",
      variantDropdownValues: ".variant-dropdown-value",
      variantDropdownContent: ".variant-dropdown-content",
      variantDropdownOptions: ".variant-dropdown-content-option",
      selectVariants: ".product-form-select-variants",
      addButton: ".product-form-submit",
    }
  }

  initVideoObservers() {
    const { productVideos } = this.$ui

    const handleIntersection = (entries) => {
      entries.forEach((entry) => {
        const video = entry.target
        if (entry.isIntersecting) {
          if (video.paused && video.classList.contains("-not-hover-played")) {
            video.play()
            video.classList.add(this.dynamicClasses.playing)
          }
        } else {
          if (!video.paused) {
            video.pause()
            video.classList.remove(this.dynamicClasses.playing)
          }
        }
      })
    }

    const videoObserver = new IntersectionObserver(
      handleIntersection,
      this.videoObserverOptions
    )

    iterable(productVideos).forEach((video) => {
      videoObserver.observe(video)
    })
  }

  initSwiper() {
    const { swiperContainer } = this.$ui
    const { playing } = this.dynamicClasses
    const slidesPerView = window.innerWidth < 768 ? 1.10666667 : 1.5

    this.swiper = new Swiper(swiperContainer, {
      slidesPerView,
      spaceBetween: 0,
      centeredSlides: true,
      loop: false, // prevent video issues
      on: {
        beforeSlideChangeStart: function () {
          iterable(swiperContainer.querySelectorAll("video")).forEach(
            (video) => {
              if (!video.paused) {
                video.pause()
                video.classList.remove(playing)
                setTimeout(() => {
                  video.currentTime = 0
                }, 200)
              }
            }
          )
          iterable(
            swiperContainer.querySelectorAll(".product-thumbnail-vimeo")
          ).forEach((vimeo) => {
            vimeo.player.getPaused().then((paused) => {
              if (!paused) {
                vimeo.player.pause()
                vimeo.classList.remove(playing)
              }
            })
          })
        },
        slideChangeTransitionEnd: function () {
          const activeSlide = this.slides[this.activeIndex]
          const autoplayVideo = activeSlide.querySelector(
            "video.-not-hover-played"
          )
          if (autoplayVideo) {
            autoplayVideo.play()
            autoplayVideo.classList.add(playing)
          }
        },
      },
    })
  }

  handleVariants() {
    const { variantRadioInputs, variantDropdownOptions } = this.$ui

    iterable(variantRadioInputs).forEach((input) => {
      input.addEventListener("click", () => {
        const valueEl = input
          .closest(".variant-radios")
          .querySelector(".variant-radios-value")
        valueEl.textContent = input.value
        this.updateOptions("radios")
        this.onVariantChange()
      })
    })

    iterable(variantDropdownOptions).forEach((option) => {
      option.addEventListener("click", () => {
        const valueEl = option
          .closest(".dropdown")
          .querySelector(".variant-dropdown-value")
        const swatchSelector = ".variant-dropdown-swatch"
        const swatchBg =
          option.querySelector(swatchSelector).style.backgroundImage
        valueEl.textContent = option.dataset.value
        valueEl.parentElement.querySelector(
          swatchSelector
        ).style.backgroundImage = swatchBg
        this.updateOptions("dropdowns")
        this.onVariantChange()
      })
    })
  }

  updateOptions(inputType) {
    const { variantRadioInputsValues, variantDropdownValues } = this.$ui

    if (inputType === "radios") {
      this.options = iterable(variantRadioInputsValues).map(
        (item) => item.textContent
      )
    } else if (inputType === "dropdowns") {
      this.options = iterable(variantDropdownValues).map(
        (item) => item.textContent
      )
    }
  }

  updateSpecifications() {
    if (!this.currentVariant) {
      return
    }

    const { productSKU } = this.$ui

    iterable(productSKU).forEach((node) => {
      node.textContent = this.currentVariant.sku
    })
  }

  onVariantChange() {
    this.updateMasterId()
    this.toggleAddButton(true, "", false)
    // this.removeErrorMessage();

    if (!this.currentVariant) {
      this.toggleAddButton(true, "", true)
      this.setUnavailable()
    } else {
      this.updateMedia()
      this.updateURL()
      this.updateVariantInput()
      this.renderProductInfo()
      this.updateSpecifications()
    }
  }

  getVariantData() {
    this.variantData =
      this.variantData ||
      JSON.parse(
        this.$el.querySelector('[type="application/json"]')?.textContent || null
      )
    return this.variantData
  }

  updateMasterId() {
    const variantData = this.getVariantData()
    if (!variantData) {
      return
    }
    this.currentVariant = variantData.find((variant) => {
      return !variant.options
        .map((option, index) => {
          return this.options[index] === option
        })
        .includes(false)
    })
  }

  toggleAddButton(disable = true, text, modifyClass = true) {
    const { addButton, soldOutIndicator } = this.$ui
    const { soldOut, visible } = this.dynamicClasses

    const addButtonText = addButton.querySelector("span")

    if (!addButton) return

    if (disable) {
      addButton.setAttribute("disabled", "disabled")
      addButton.classList.add(soldOut)
      soldOutIndicator.classList.add(visible)
      if (text) addButtonText.textContent = text
    } else {
      addButton.removeAttribute("disabled")
      addButton.classList.remove(soldOut)
      soldOutIndicator.classList.remove(visible)
      addButtonText.textContent = window.variantStrings.addToCart
    }

    if (!modifyClass) return
  }

  setUnavailable() {
    const { addButton } = this.$ui
    // const productForm = document.getElementById(
    //   `product-form-${this.$el.dataset.section}`
    // )
    const addButtonText = addButton.querySelector("span")
    const price = document.getElementById(`price-${this.$el.dataset.section}`)
    if (!addButton) return
    addButtonText.textContent = window.variantStrings.unavailable
    if (price) price.classList.add("visibility-hidden")
  }

  updateMedia() {
    if (!this.currentVariant) return
    if (!this.currentVariant.featured_media) return
    const newMedia = document.querySelector(
      `[data-media-id="${this.$el.dataset.section}-${this.currentVariant.featured_media.id}"]`
    )
    if (!newMedia) return
    const parent = newMedia.parentElement
    if (window.innerWidth >= 1024) {
      if (parent.firstChild == newMedia) return
      parent.prepend(newMedia)
      window.scrollTo(0, 0)
    } else {
      if (!this.swiper) return
      const newMediaIndex = Array.from(parent.children).indexOf(newMedia)
      this.swiper.slideTo(newMediaIndex)
    }
  }

  updateURL() {
    if (!this.currentVariant) return
    window.history.replaceState(
      {},
      "",
      `${this.$el.dataset.url}?variant=${this.currentVariant.id}`
    )
  }

  updateVariantInput() {
    const input = this.$el.querySelector('input[name="id"]')
    input.value = this.currentVariant.id
    input.dispatchEvent(new Event("change", { bubbles: true }))
  }

  renderProductInfo() {
    fetch(
      `${this.$el.dataset.url}?variant=${this.currentVariant.id}&section_id=${this.$el.dataset.section}`
    )
      .then((response) => response.text())
      .then((responseText) => {
        const id = `price-${this.$el.dataset.section}`
        const html = new DOMParser().parseFromString(responseText, "text/html")
        const destination = document.getElementById(id)
        const source = html.getElementById(id)

        if (source && destination) destination.innerHTML = source.innerHTML

        const price = document.getElementById(
          `price-${this.$el.dataset.section}`
        )

        if (price) price.classList.remove("visibility-hidden")
        this.toggleAddButton(
          !this.currentVariant.available,
          window.variantStrings.soldOut
        )
      })
  }

  initStickyBar() {
    const { productFormInner, selectVariants } = this.$ui
    const { stickyBarVisible } = this.dynamicClasses
    const section = this.$el.parentElement

    if (!productFormInner) {
      return
    }

    const productFormObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.intersectionRatio > 0) {
            if (section.classList.contains(stickyBarVisible)) {
              section.classList.remove(stickyBarVisible)
            }
          } else {
            if (!section.classList.contains(stickyBarVisible)) {
              section.classList.add(stickyBarVisible)
            }
          }
        })
      },
      {
        rootMargin: "-50px",
      }
    )

    productFormObserver.observe(productFormInner)
    productFormInner.style.minHeight = `${productFormInner.offsetHeight}px`

    if (selectVariants) {
      selectVariants.addEventListener("click", () => {
        anime({
          targets: [document.documentElement, document.body],
          scrollTop: 0,
          duration: 900,
          easing: "easeInOutQuart",
        })
      })
    }
  }

  initImageZoom() {
    const { productImages } = this.$ui
    const { zoom, customCursorVisible, zoomImgLoaded } = this.dynamicClasses

    if (productImages) {
      iterable(productImages).forEach((item) => {
        const zoomEl = item.querySelector(".product-images-item-zoom")
        if (!zoomEl) {
          return
        }

        item.addEventListener("mousemove", (e) => {
          let x, y

          if (
            typeof e.offsetX === "undefined" ||
            typeof e.offsetY === "undefined"
          ) {
            return
          }

          x = (e.offsetX / zoomEl.offsetWidth) * 100
          y = (e.offsetY / zoomEl.offsetHeight) * 100

          item.style.setProperty("--background-position", `${x}% ${y}%`)
          item.style.setProperty(
            "--cursor-position",
            `translate(${e.offsetX}px, ${e.offsetY}px)`
          )
        })

        item.addEventListener("mouseenter", (e) => {
          item.classList.add(customCursorVisible)
          item.classList.add(zoomImgLoaded)
        })

        item.addEventListener("mouseleave", (e) => {
          item.classList.remove(customCursorVisible)
        })

        item.addEventListener("click", (e) => {
          item.classList.toggle(zoom)
        })
      })
    }
  }

  setupVimeo() {
    const { swiperContainer } = this.$ui
    const vimeoEmbeds = swiperContainer.querySelectorAll(
      ".product-thumbnail-vimeo"
    )
    iterable(vimeoEmbeds).forEach((embed) => {
      const player = new Vimeo(embed.id, {
        id: embed.dataset.externalId,
        controls: false,
        muted: true,
      })
      embed.player = player

      if (window.innerWidth >= 1024) {
        embed.addEventListener("mouseenter", () => {
          player.play()
        })

        embed.addEventListener("mouseleave", () => {
          player.pause()
        })
      }
    })
  }

  checkRadiosNav() {
    const { variantRadiosNavButtons } = this.$ui
    iterable(variantRadiosNavButtons).forEach((button) => {
      const slider = button.previousElementSibling
      const showNav = slider.scrollWidth > Math.min(window.innerWidth, 470)
      button.style.display = showNav ? "" : "none"
    })
  }

  handleScroll(position) {
    this.setInfoPosition(position)
  }

  setInfoPosition(position) {
    const { productColumns, productInfo } = this.$ui
    const { fixed, sticky } = this.dynamicClasses
    const headerHeight = 100
    const bottomMargin = 100
    const diff = productInfo.offsetHeight - (window.innerHeight - headerHeight)
    let offset = 0
    let isFixed = false
    let isSticky = false

    if (diff > 0) {
      isFixed = position > diff + bottomMargin
      offset = Math.max(
        position -
          (productColumns.offsetHeight - window.innerHeight + headerHeight),
        0
      )
    } else {
      isSticky = true
    }

    productInfo.classList.toggle(fixed, isFixed)
    productInfo.classList.toggle(sticky, isSticky)
    productInfo.style.setProperty("--offset-y", `${offset}px`)
  }

  attachEvents() {
    const {
      addButton,
      swiperContainer,
      productVideos,
      variantDropdownToggle,
      variantRadiosNavButtons,
    } = this.$ui
    const { soldOut, playing, open, activated } = this.dynamicClasses

    if (addButton) {
      addButton.addEventListener("click", (e) => {
        if (addButton.classList.contains(soldOut)) {
          const modal = document.querySelector(
            "#modal-contact-customer-service"
          )
          modal.classList.add("-open")
          e.preventDefault()
        }
      })
    }

    // preorder
    const buttons = this.$el.querySelector(".product-form-buttons")
    if (buttons) {
      buttons.addEventListener("click", (e) => {
        if (e.target.closest(".gPreorderBtn")) {
          const modal = document.querySelector("#modal-preorder")
          modal.classList.add("-open")
          e.preventDefault()
        }
      })
    }

    if (window.innerWidth >= 1024) {
      iterable(productVideos).forEach((video) => {
        video.removeAttribute("controls")
        video.addEventListener("mouseenter", () => {
          if (video.paused && !video.classList.contains("-not-hover-played")) {
            video.play()
          }
        })
        video.addEventListener("mouseleave", () => {
          if (!video.paused && !video.classList.contains("-not-hover-played")) {
            video.pause()
          }
        })
      })

      window.addEventListener(
        "resize",
        debounce(() => {
          this.setInfoPosition(window.pageYOffset)
        })
      )
    } else {
      swiperContainer.addEventListener("click", (e) => {
        if (
          e.target.tagName.toLowerCase() === "video" &&
          !e.target.classList.contains("-not-hover-played")
        ) {
          const video = e.target
          if (video.paused) {
            video.play()
            video.classList.add(playing)
          } else {
            video.pause()
            video.classList.remove(playing)
          }
        } else if (e.target.classList.contains("product-thumbnail-vimeo")) {
          e.target.classList.toggle(playing)
          e.target.player.getPaused().then((paused) => {
            if (paused) {
              e.target.player.play()
            } else {
              e.target.player.pause()
            }
          })
        }
      })

      iterable(variantRadiosNavButtons).forEach((button) => {
        button.addEventListener("click", () => {
          const slider = button.previousElementSibling
          anime({
            targets: slider,
            scrollLeft: 52,
            duration: 400,
            easing: "easeOutQuad",
          })
          button.classList.add(activated)
        })
      })
    }
  }

  created() {
    if (window.innerWidth < 1024) {
      this.initSwiper()
      this.checkRadiosNav()
      this.updateOptions("radios")
    } else {
      this.initStickyBar()
      this.initImageZoom()
      scrollLoop(() => {
        this.handleScroll(window.pageYOffset)
      })
      this.updateOptions("dropdowns")
    }
    this.setupVimeo()
    this.handleVariants()
    this.attachEvents()
    this.updateMasterId()
    this.updateSpecifications()
    this.initVideoObservers()
  }
}
